import { useEntityCache } from '@/contexts/EntityCacheContext'
import { BBDTag, MaterialIcon } from '@gravitate-js/excalibrr'
import { Popover } from 'antd'

export type MappingStatusFilter =
  | 'mapped'
  | 'needs mapping'
  | 'unused'
  | 'not found in sync'

export interface MRDCellProps {
  /** GOIDs to display as tags */
  goids: string[] | null
  /** Entity type for name lookup - only used for tank special case */
  objectType?: string
  /** Maximum number of tags to show before collapsing into "+N more" */
  maxVisible?: number
  /**
   * Mapping status - controls empty state display.
   * - 'unused': shows "Not Setup"
   * - 'mapped': shows warning if goids is empty
   * - 'needs mapping': normal display
   */
  status?: MappingStatusFilter
}

/**
 * MRDCell - Component to render master reference data GOIDs as entity name tags
 * Looks up entity names from the global entity cache and displays as tags
 * Shows truncated view with "+N more" popover when tags exceed maxVisible
 */
export function MRDCell({
  goids,
  objectType,
  maxVisible = 2,
  status,
}: MRDCellProps) {
  const notSetup = status === 'unused'
  const warnIfEmpty = status === 'needs mapping'
  const { getEntity } = useEntityCache()

  if (notSetup) return 'Not Setup'

  // Handle empty or null mappings
  if (!goids || goids.length === 0) {
    if (warnIfEmpty) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--theme-error)',
            }}
          >
            <MaterialIcon icon='warning' size={16} />
            No Mapping
          </span>
        </div>
      )
    }
    return <span style={{ color: 'var(--gray-6)' }}></span>
  }

  /** Get display text for a GOID by looking up entity name in cache */
  const getDisplayText = (goid: string): string => {
    const entity = getEntity('', goid)
    if (entity?.name) {
      // Tank chips also surface the tank's size when known (unit-agnostic)
      if (objectType === 'tank' && entity.tank_size != null) {
        return `${entity.name} (size ${entity.tank_size.toLocaleString()}) (${goid})`
      }
      return `${entity.name} (${goid})`
    }
    return goid
  }

  // Helper to render a single tag
  const renderTag = (goid: string, index: number) => (
    <BBDTag key={index} theme2 style={{ margin: 0 }}>
      {getDisplayText(goid)}
    </BBDTag>
  )

  // Split goids into visible and overflow
  const visibleGoids = goids.slice(0, maxVisible)
  const overflowCount = goids.length - maxVisible
  const hasOverflow = overflowCount > 0

  // Popover content showing all tags
  const popoverContent = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        maxWidth: '400px',
        maxHeight: '300px',
        overflowY: 'auto',
      }}
    >
      {goids.map((goid, index) => renderTag(goid, index))}
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center',
        padding: '4px 0',
      }}
    >
      {visibleGoids.map((goid, index) => renderTag(goid, index))}
      {hasOverflow && (
        <Popover
          content={popoverContent}
          title='All Mappings'
          trigger='hover'
          placement='bottom'
        >
          <BBDTag
            theme2
            style={{
              margin: 0,
              cursor: 'pointer',
              borderStyle: 'dashed',
            }}
          >
            +{overflowCount} more
          </BBDTag>
        </Popover>
      )}
    </div>
  )
}
